import type { PermitTypeContext } from "@offstreetparking/domain/rules";
import { DateTime } from "luxon";
import { AccessibleContent } from "../../accessibility";
import { formatDate, formatTimeOfDay } from "../../formatters";
import { ArrowRightIcon } from "../../icons/ArrowRightIcon";
import { CalendarClockIcon } from "../../icons/CalendarClockIcon";
import { useDesignSystemI18n } from "../../providers/DesignSystemI18nContext";
import { useLocale } from "../../providers/DesignSystemLocaleContext";
import { Text } from "../../typography";
import { TimeBadge } from "./TimeBadge";
import { type ComparisonSide, changedValueClassName, valueIntent } from "./comparison";

const DAY_KEYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"] as const;

type ParkingOptionProps = {
	timezone: string;
	isInstant: boolean;
	context?: PermitTypeContext | null;
	comparison?: { side: ComparisonSide; other: { context?: PermitTypeContext | null } };
};

type Rules = PermitTypeContext | null | undefined;

type DayOfWeekField = NonNullable<NonNullable<PermitTypeContext["start"]>["dayOfWeek"]>;

const toDayArray = (value: DayOfWeekField | undefined): string[] =>
	Array.isArray(value) ? value : value ? [value] : [];

const parseDateInTimezone = ({ date, timezone }: { date: string; timezone: string }) =>
	DateTime.fromISO(date, { zone: timezone }).toJSDate();

const splitDurationFixed = (durationInMinutes: number | undefined) => {
	const total = durationInMinutes ?? 0;
	return {
		weeks: Math.floor(total / (24 * 60 * 7)),
		days: Math.floor((total % (24 * 60 * 7)) / (24 * 60)),
		hours: Math.floor((total % (24 * 60)) / 60),
		minutes: total % 60,
	};
};

const DayOfWeekBadges = ({ days, labels }: { days: string[]; labels: Record<(typeof DAY_KEYS)[number], string> }) => {
	const lowerDays = days.map((d) => d.toLowerCase());
	return (
		<div className="flex flex-wrap gap-1">
			{DAY_KEYS.filter((key) => lowerDays.includes(key)).map((key) => (
				<div key={key} className="flex items-center justify-center rounded-md bg-info-faded px-2 py-0.5">
					<Text size="xs" weight="medium" className="text-info">
						{labels[key]}
					</Text>
				</div>
			))}
		</div>
	);
};

export const RulesPreview = ({ timezone, isInstant, context, comparison }: ParkingOptionProps) => {
	const { daysOfWeekShort, parkingOption } = useDesignSystemI18n();
	const { locale } = useLocale();
	const start = context?.start;
	const expiry = context?.expiry;
	const startDayOfWeek = toDayArray(start?.dayOfWeek);
	const expiryDayOfWeek = toDayArray(expiry?.dayOfWeek);
	const isFullyInstant = (!!start?.isDateNow && !!start?.isTimeNow) || isInstant;
	const side = comparison?.side;
	const textIntent = valueIntent(side);

	const hasDuration = (rules: Rules) => {
		const duration = splitDurationFixed(rules?.duration?.fixed);
		return !!(duration.minutes || duration.hours || duration.days || duration.weeks);
	};

	const getStartDateContent = (rules: Rules) => {
		if (rules?.start?.date) {
			return formatDate({
				date: parseDateInTimezone({ date: rules.start.date, timezone }),
				formatType: "Abbreviated_Date",
				timezone,
			});
		}

		if (toDayArray(rules?.start?.dayOfWeek).length) {
			return parkingOption.specificDays;
		}

		if (rules?.start?.isDateNow || isInstant) {
			return parkingOption.whenRegistered;
		}

		return parkingOption.userSelects;
	};

	const getStartTimeContent = (rules: Rules) => {
		if (rules?.start?.time) {
			return formatTimeOfDay({ time: rules.start.time, locale });
		}

		if (rules?.start?.isTimeNow || isInstant) {
			return parkingOption.whenRegistered;
		}

		return parkingOption.userSelects;
	};

	const getExpiryDateContent = (rules: Rules) => {
		if (toDayArray(rules?.expiry?.dayOfWeek).length) {
			return parkingOption.specificDays;
		}
		if (rules?.expiry?.date) {
			return formatDate({
				date: parseDateInTimezone({ date: rules.expiry.date, timezone }),
				formatType: "Abbreviated_Date",
				timezone,
			});
		}

		if (hasDuration(rules)) {
			return parkingOption.afterDuration;
		}

		if (rules?.expiry?.sameDay) {
			return parkingOption.sameDay;
		}

		if (rules?.expiry?.inDays) {
			return parkingOption.afterDays(rules.expiry.inDays);
		}

		return parkingOption.userSelects;
	};

	const getExpiryTimeContent = (rules: Rules) => {
		if (rules?.expiry?.time) {
			return formatTimeOfDay({ time: rules.expiry.time, locale });
		}

		if (hasDuration(rules)) {
			return parkingOption.duration(splitDurationFixed(rules?.duration?.fixed));
		}

		return parkingOption.userSelects;
	};

	const isChanged = (getContent: (rules: Rules) => string) =>
		!!comparison && getContent(context) !== getContent(comparison.other.context);

	const startLabel = isFullyInstant
		? getStartDateContent(context)
		: `${getStartDateContent(context)} ${getStartTimeContent(context)}`;

	return (
		<AccessibleContent label={`${startLabel} – ${getExpiryDateContent(context)} ${getExpiryTimeContent(context)}`}>
			<div className="space-y-2">
				<div className="flex items-start gap-2">
					<CalendarClockIcon className="mt-[1px] size-4" />
					<div className="flex flex-col items-start gap-1">
						<div className="flex items-center gap-2">
							<Text
								size="sm"
								weight="bold"
								intent={textIntent}
								className={changedValueClassName(side, isChanged(getStartDateContent))}
							>
								{getStartDateContent(context)}
							</Text>
						</div>
						{!!startDayOfWeek.length && <DayOfWeekBadges days={startDayOfWeek} labels={daysOfWeekShort} />}
						{!isFullyInstant && (
							<TimeBadge side={side} changed={isChanged(getStartTimeContent)}>
								{getStartTimeContent(context)}
							</TimeBadge>
						)}
					</div>

					<ArrowRightIcon className="mt-[2px] size-4 shrink-0" />

					<div className="flex flex-col items-start gap-1">
						<div className="flex items-center gap-2">
							<Text
								size="sm"
								weight="bold"
								intent={textIntent}
								className={changedValueClassName(side, isChanged(getExpiryDateContent))}
							>
								{getExpiryDateContent(context)}
							</Text>
						</div>
						{!!expiryDayOfWeek.length && (
							<DayOfWeekBadges days={expiryDayOfWeek} labels={daysOfWeekShort} />
						)}
						<TimeBadge side={side} changed={isChanged(getExpiryTimeContent)}>
							{getExpiryTimeContent(context)}
						</TimeBadge>
					</div>
				</div>
			</div>
		</AccessibleContent>
	);
};
